import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { api } from '../utils/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Send, AlertCircle, ArrowRight, Wallet, CheckCircle, ArrowDownUp, Gift, Trophy } from 'lucide-react';

const CURRENCIES = [
  { code: 'JPY', symbol: '¥', flag: '🇯🇵', label: 'JPY' },
  { code: 'BRL', symbol: 'R$', flag: '🇧🇷', label: 'BRL' },
];

export default function SendMoneyPage() {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('JPY');
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [createdTransfer, setCreatedTransfer] = useState(null);
  const calcTimer = useRef(null);

  const balance = user?.balance || 0;
  const transferLimit = user?.transfer_limit || null;

  useEffect(() => {
    fetchRecipients();
  }, []);

  useEffect(() => {
    if (calcTimer.current) clearTimeout(calcTimer.current);
    if (amount && parseFloat(amount) > 0) {
      calcTimer.current = setTimeout(() => calculateAmount(), 300);
    } else {
      setCalculation(null);
    }
    return () => { if (calcTimer.current) clearTimeout(calcTimer.current); };
  }, [amount, currency]);

  const fetchRecipients = async () => {
    try {
      const response = await api.getRecipients();
      setRecipients(response.data);
    } catch (error) {
      console.error('Failed to fetch recipients:', error);
    }
  };

  const calculateAmount = async () => {
    try {
      const response = await api.calculateExchange(parseFloat(amount), currency);
      setCalculation(response.data);
    } catch (error) {
      console.error('Failed to calculate:', error);
    }
  };

  const isBalanceSufficient = calculation ? balance >= calculation.total_jpy : true;
  const isFeeExceedsAmount = calculation && currency === 'JPY' && calculation.net_jpy <= 0;
  const isOverLimit = transferLimit && calculation ? calculation.amount_jpy > transferLimit : false;

  const toggleCurrency = () => {
    setCurrency(prev => prev === 'JPY' ? 'BRL' : 'JPY');
    setAmount('');
    setCalculation(null);
  };

  const activeCurr = CURRENCIES.find(c => c.code === currency);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (user?.kyc_status !== 'approved') {
      toast.error(t('send_kyc_required'));
      navigate('/kyc');
      return;
    }

    if (!selectedRecipient) {
      toast.error(t('send_select_recipient'));
      return;
    }

    if (!isBalanceSufficient) {
      toast.error(t('send_insufficient_balance'));
      return;
    }

    if (isFeeExceedsAmount) {
      toast.error(t('send_fee_exceeds'));
      return;
    }

    if (isOverLimit) {
      toast.error(t('send_over_limit'));
      return;
    }

    setLoading(true);

    try {
      const response = await api.createTransfer({
        recipient_id: selectedRecipient,
        amount_jpy: calculation.amount_jpy,
        amount_brl: calculation.amount_brl,
        exchange_rate: calculation.exchange_rate,
        fee_jpy: calculation.fee_jpy,
        total_jpy: calculation.total_jpy
      });
      
      setCreatedTransfer(response.data);
      toast.success(t('send_success_toast'));
      setStep(2);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('send_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl" data-testid="send-money-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            {t('send_title')}
          </h1>
          <p className="text-muted-foreground">
            {t('send_subtitle')}
          </p>
        </div>

        {/* Balance Display */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-5 mb-6 text-white" data-testid="send-balance-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wallet className="h-5 w-5 text-slate-400" />
              <span className="text-sm text-slate-400">{t('send_balance')}</span>
            </div>
            <div className="text-2xl font-bold font-mono" data-testid="send-balance-amount">
              ¥{balance.toLocaleString()}
            </div>
          </div>
          {calculation && !isBalanceSufficient && (
            <div className="mt-3 flex items-center gap-2 text-red-400 text-sm" data-testid="insufficient-balance-warning">
              <AlertCircle className="h-4 w-4" />
              <span>{(t('send_insufficient') || '').replace('{amount}', (calculation.total_jpy - balance).toLocaleString())}</span>
            </div>
          )}
          {transferLimit && (
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-slate-400">{t('send_transfer_limit')}</span>
              <span className="font-mono text-slate-300" data-testid="send-transfer-limit">¥{transferLimit.toLocaleString()}</span>
            </div>
          )}
          {isOverLimit && (
            <div className="mt-2 flex items-center gap-2 text-red-400 text-sm" data-testid="over-limit-warning">
              <AlertCircle className="h-4 w-4" />
              <span>{t('send_over_limit_msg').replace('{limit}', transferLimit.toLocaleString())}</span>
            </div>
          )}
        </div>

        {user?.kyc_status !== 'approved' && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6 mb-8" data-testid="kyc-warning">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-1">{t('send_kyc_required_title')}</h3>
                <p className="text-sm text-amber-800">
                  {t('send_kyc_required_desc')}
                </p>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="bg-card border border-border/50 rounded-2xl p-8">
              {/* Step 1: Amount with Currency Selector */}
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-full bg-accent text-white font-bold flex items-center justify-center text-sm">1</div>
                  <h2 className="font-heading text-xl font-semibold text-primary">
                    {t('send_step1')}
                  </h2>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">{t('send_amount_label')}</Label>
                  <div className="flex gap-2 items-stretch">
                    {/* Currency Toggle */}
                    <button
                      type="button"
                      onClick={toggleCurrency}
                      className="flex items-center gap-2 px-4 bg-secondary hover:bg-secondary/80 border border-border rounded-lg transition-colors shrink-0"
                      data-testid="currency-toggle-btn"
                    >
                      <span className="text-lg">{activeCurr.flag}</span>
                      <span className="font-semibold text-sm">{activeCurr.code}</span>
                      <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                    {/* Amount Input */}
                    <div className="relative flex-1">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
                        {activeCurr.symbol}
                      </span>
                      <Input
                        id="amount"
                        type="number"
                        placeholder={currency === 'JPY' ? '10,000' : '500'}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="pl-10 h-14 text-lg"
                        min="1"
                        step="any"
                        required
                        data-testid="amount-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {currency === 'JPY' ? t('send_currency_hint_jpy') : t('send_currency_hint_brl')}
                  </p>
                </div>

                {/* Calculation Result */}
                {calculation && (
                  <div className="mt-6 p-6 bg-secondary/30 rounded-xl space-y-3" data-testid="calculation-result">
                    {isFeeExceedsAmount ? (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle className="h-4 w-4" />
                        <span>{t('send_fee_exceeds')}</span>
                      </div>
                    ) : (
                      <>
                        {calculation.is_free_transfer && (
                          <div className="flex items-center gap-2 text-emerald-600 text-sm bg-emerald-500/10 p-2 rounded-lg" data-testid="free-transfer-badge">
                            <Gift className="h-4 w-4" />
                            <span>{t('send_free_transfer')}</span>
                          </div>
                        )}
                        {!calculation.is_free_transfer && calculation.fee_discount > 0 && (
                          <div className="flex items-center gap-2 text-accent text-sm bg-accent/10 p-2 rounded-lg" data-testid="tier-discount-badge">
                            <Trophy className="h-4 w-4" />
                            <span>{(t('send_tier_discount') || '').replace('{percent}', calculation.fee_discount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('send_input_amount')}</span>
                          <span className="font-medium font-mono">
                            {currency === 'JPY'
                              ? `¥${calculation.amount_jpy.toLocaleString()}`
                              : `R$ ${calculation.amount_brl.toLocaleString()}`
                            }
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('send_fee')}</span>
                          <span className="font-medium font-mono text-red-500">-¥{calculation.fee_jpy.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{t('send_net_amount')}</span>
                          <span className="font-medium font-mono">¥{calculation.net_jpy.toLocaleString()}</span>
                        </div>
                        <div className="border-t border-border pt-3 flex justify-between">
                          <span className="text-muted-foreground font-medium">{t('send_total_debit')}</span>
                          <span className={`text-xl font-bold font-mono ${isBalanceSufficient ? 'text-primary' : 'text-red-600'}`}>
                            ¥{calculation.total_jpy.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>{t('send_deduct')}</span>
                          <span className="font-mono">
                            ¥{balance.toLocaleString()} → ¥{Math.max(0, balance - calculation.total_jpy).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between pt-3 border-t border-border">
                          <span className="text-muted-foreground font-medium">{t('send_receive')}</span>
                          <span className="text-2xl font-bold font-mono text-accent">R$ {calculation.amount_brl.toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-muted-foreground pt-2">
                          {t('send_rate')}: 1 JPY = {calculation.exchange_rate.toFixed(5)} BRL
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Step 2: Recipient */}
              <div className="mt-8 pt-8 border-t border-border">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-full bg-accent text-white font-bold flex items-center justify-center text-sm">2</div>
                  <h2 className="font-heading text-xl font-semibold text-primary">
                    {t('send_step2')}
                  </h2>
                </div>

                {recipients.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">{t('send_no_recipients')}</p>
                    <Button type="button" variant="outline" onClick={() => navigate('/recipients')} data-testid="add-recipient-button">
                      {t('send_add_recipient')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="recipient">{t('send_recipient_label')}</Label>
                    <Select value={selectedRecipient} onValueChange={setSelectedRecipient} required>
                      <SelectTrigger className="h-12" data-testid="recipient-select">
                        <SelectValue placeholder={t('send_select_recipient')} />
                      </SelectTrigger>
                      <SelectContent>
                        {recipients.map((recipient) => (
                          <SelectItem key={recipient.id} value={recipient.id} data-testid={`recipient-option-${recipient.id}`}>
                            {recipient.full_name} - {recipient.account_type === 'pix' ? 'PIX' : t('bank_account')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full rounded-full h-14 text-lg"
              disabled={loading || !calculation || !selectedRecipient || !isBalanceSufficient || isFeeExceedsAmount || user?.kyc_status !== 'approved'}
              data-testid="submit-transfer-button"
            >
              {loading ? t('send_btn_processing') : !isBalanceSufficient ? t('send_btn_insufficient') : t('send_btn_submit')}
              {!loading && isBalanceSufficient && <ArrowRight className="ml-2 h-5 w-5" />}
            </Button>

            {calculation && !isBalanceSufficient && (
              <Button type="button" variant="outline" size="lg" className="w-full rounded-full h-12" onClick={() => navigate('/deposit')} data-testid="go-deposit-button">
                {t('send_go_deposit')}
              </Button>
            )}
          </form>
        )}

        {/* Success - Step 2 */}
        {step === 2 && createdTransfer && (
          <div className="bg-card border border-border/50 rounded-2xl p-8" data-testid="transfer-success">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-primary mb-2">
                {t('send_success_title')}
              </h2>
              <p className="text-muted-foreground">{t('send_success_desc')}</p>
            </div>

            <div className="space-y-4 p-6 bg-secondary/30 rounded-xl mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('send_id')}</span>
                <span className="font-mono text-xs">{createdTransfer.id.substring(0, 8).toUpperCase()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('send_fee')}</span>
                <span className="font-mono">-¥{createdTransfer.fee_jpy.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-border pt-3">
                <span className="text-muted-foreground font-medium">{t('send_total_deducted')}</span>
                <span className="font-mono font-bold text-primary">¥{createdTransfer.total_jpy.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-border pt-3">
                <span className="text-muted-foreground font-medium">{t('send_receive')}</span>
                <span className="text-xl font-bold font-mono text-accent">R$ {createdTransfer.amount_brl.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/history')} data-testid="view-history-button">
                {t('send_go_history')}
              </Button>
              <Button className="flex-1" onClick={() => { setStep(1); setCreatedTransfer(null); setAmount(''); setSelectedRecipient(''); setCalculation(null); }} data-testid="new-transfer-button">
                {t('send_new')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
